
import { ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { Button } from "@/components/ui/button";
import { clearAllAuthStorage } from "@/utils/authStorage";

type ProtectedRouteProps = {
  children: ReactNode;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, isLoading, profile } = useAuth();
  const { buyerProfile, isLoading: buyerLoading } = useBuyer();
  const location = useLocation();
  const mountTimeRef = useRef(Date.now());
  const [isTimedOut, setIsTimedOut] = useState(false);

  // Global loading timeout - prevents infinite loading states
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading || buyerLoading) {
        console.warn("🛡️ Global loading timeout reached (10s) - still loading:", { isLoading, buyerLoading });
        setIsTimedOut(true);
      }
    }, 10000); // 10 second global timeout

    return () => clearTimeout(timeout);
  }, []); // Only run once on mount

  // Reset timeout state when loading completes
  useEffect(() => {
    if (!isLoading && !buyerLoading) {
      setIsTimedOut(false);
    }
  }, [isLoading, buyerLoading]);

  useEffect(() => {
    const timeElapsed = Date.now() - mountTimeRef.current;
    console.log("🛡️ ProtectedRoute mounted", {
      path: location.pathname,
      user: !!user,
      profile: !!profile,
      buyerProfile: !!buyerProfile,
      isLoading,
      buyerLoading,
      timeElapsed
    });
  }, [user, profile, buyerProfile, isLoading, buyerLoading, location.pathname]);

  // Handle timeout state - show recovery UI
  if (isTimedOut && (isLoading || buyerLoading)) {
    const handleRetry = () => {
      console.log("🛡️ User clicked retry after timeout");
      window.location.reload();
    };

    const handleGoToLogin = () => {
      console.log("🛡️ User clicked go to login after timeout");
      clearAllAuthStorage();
      const isBuyerRoute = location.pathname.startsWith('/buyer');
      window.location.href = isBuyerRoute ? "/buyer/login" : "/seller/login";
    };

    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-4 max-w-md px-4">
          <p className="text-lg text-foreground">
            Taking longer than expected...
          </p>
          <p className="text-sm text-muted-foreground">
            Please check your internet connection or try again.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={handleRetry} className="bg-godyspo-gold text-godyspo-navy hover:bg-godyspo-gold/90">
              Retry
            </Button>
            <Button variant="outline" onClick={handleGoToLogin}>
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to the appropriate login page based on route
  if (!user && !isLoading) {
    console.log("🛡️ User not authenticated, redirecting to login");
    const isBuyerRoute = location.pathname.startsWith('/buyer');
    const redirectTo = isBuyerRoute ? "/buyer/login" : "/seller/login";
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Show loading while auth or profile data is still loading
  if (isLoading || buyerLoading || !user) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godyspo-gold mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // At this point, we have a user and profile data has finished loading
  const hasBothProfiles = Boolean(buyerProfile && profile);
  const isBuyerRoute = location.pathname.startsWith('/buyer');
  const isSellerRoute = location.pathname.startsWith('/seller');

  console.log("🛡️ Route protection check", {
    hasBothProfiles,
    isBuyerRoute,
    isSellerRoute,
    hasProfile: !!profile,
    hasBuyerProfile: !!buyerProfile
  });

  // Handle route-specific access for dual-profile users
  if (hasBothProfiles) {
    console.log("🛡️ User has both profiles, allowing access");
    return <>{children}</>;
  }

  // For seller routes, give additional time for profile to load after successful auth
  if (isSellerRoute && !profile) {
    console.log("🛡️ Seller route but no seller profile yet");
    
    // If we have a buyer profile, redirect to buyer dashboard
    if (buyerProfile) {
      console.log("🛡️ Has buyer profile, redirecting to buyer dashboard");
      return <Navigate to="/buyer/dashboard" replace />;
    }
    
    // If no profiles at all, show loading a bit longer to allow profile fetch
    const timeElapsed = Date.now() - mountTimeRef.current;
    if (timeElapsed < 5000) { // Give 5 seconds for profile to load (increased from 2s)
      console.log("🛡️ Waiting for seller profile to load...", { timeElapsed });
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godyspo-gold mx-auto"></div>
            <p className="text-sm text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      );
    }
    
    // After timeout, redirect to registration
    console.log("🛡️ No seller profile found after timeout, redirecting to registration");
    return <Navigate to="/seller/register" replace />;
  }

  // Handle single-profile scenarios for buyer routes
  if (isBuyerRoute && !buyerProfile) {
    console.log("🛡️ Buyer route but no buyer profile yet");
    
    // If user has a seller profile, redirect to seller dashboard
    if (profile) {
      console.log("🛡️ Has seller profile, redirecting to seller dashboard");
      return <Navigate to="/seller/dashboard" replace />;
    }
    
    // Give time for buyer profile to load (same pattern as seller routes)
    const timeElapsed = Date.now() - mountTimeRef.current;
    if (timeElapsed < 5000) {
      console.log("🛡️ Waiting for buyer profile to load...", { timeElapsed });
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-godyspo-gold mx-auto"></div>
            <p className="text-sm text-muted-foreground">Loading profile...</p>
          </div>
        </div>
      );
    }
    
    // After timeout with no profile, redirect to registration
    console.log("🛡️ No buyer profile found after timeout, redirecting to registration");
    return <Navigate to="/buyer/register" replace />;
  }

  console.log("🛡️ Access granted");
  return <>{children}</>;
};

export default ProtectedRoute;
